import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { confirmOutcome } from "./stripeReturn";

// The card form of the payment pages: Stripe's Payment Element for a
// PaymentIntent the API minted on the school's account, in a dialog.
//
// Why not StripePaymentPortal: it confirms without a return_url, so a
// payment method that redirects (a bank, a wallet) can't come back, and
// these pages need Stripe to bring the customer back to the page they paid
// on. Like it, the dialog renders in the document, not in the element's
// shadow root: Stripe's iframes and its 3-D Secure frame belong in the
// light DOM, so it's styled inline (the element's stylesheet doesn't reach
// it) rather than through a shadow portal.

export type StripePaymentDialogProps = {
  /** The platform's publishable key, as the API gives it. */
  publishableKey: string;
  clientSecret: string;
  /** The school's connected account, for a direct charge. */
  stripeAccount?: string;
  /** "£45" — what the button pays. */
  amountLabel: string;
  /** Where Stripe sends the customer back after a redirect: this page, on
   * this host, with its link query. */
  returnUrl: string;
  primaryColor?: string;
  onPaid: (paymentIntentId?: string) => void;
  onClose: () => void;
};

const font = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    fontFamily: font,
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "480px",
    width: "100%",
    margin: "16px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
  title: { fontSize: "20px", fontWeight: 600, color: "#111827", margin: 0 },
  close: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px",
    color: "#6b7280",
    fontSize: "24px",
    lineHeight: 1,
  },
  error: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "8px",
    fontSize: "14px",
    marginTop: "12px",
  },
};

const submitStyle = (color: string, disabled: boolean): CSSProperties => ({
  width: "100%",
  marginTop: "20px",
  padding: "14px 24px",
  backgroundColor: disabled ? "#9ca3af" : color,
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  fontSize: "16px",
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: font,
});

export function StripePaymentDialog(props: StripePaymentDialogProps) {
  const { publishableKey, stripeAccount, clientSecret, onClose } = props;
  const primary = props.primaryColor || "#111827";
  // One Stripe instance for this payment: the dialog mounts per payment.
  const [stripe] = useState(() => loadStripe(publishableKey, stripeAccount ? { stripeAccount } : undefined));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  if (typeof document === "undefined") return null;
  return createPortal(
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div style={styles.panel} role="dialog" aria-modal="true" aria-labelledby="baluu-secure-payment-title">
        <div style={styles.header}>
          <h2 id="baluu-secure-payment-title" style={styles.title}>
            Payment Details
          </h2>
          <button type="button" style={styles.close} onClick={onClose} disabled={busy} aria-label="Close">
            ×
          </button>
        </div>
        <Elements
          stripe={stripe}
          options={{
            clientSecret,
            appearance: { theme: "stripe", variables: { colorPrimary: primary, fontFamily: font } },
          }}
        >
          <PaymentForm {...props} primary={primary} onBusy={setBusy} />
        </Elements>
      </div>
    </div>,
    document.body,
  );
}

function PaymentForm({
  amountLabel,
  returnUrl,
  primary,
  onPaid,
  onBusy,
}: StripePaymentDialogProps & { primary: string; onBusy: (busy: boolean) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    onBusy(true);
    setError(null);
    try {
      // A card that needs 3-D Secure is checked in Stripe's own frame here;
      // a method that has to leave the page comes back to `returnUrl`.
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });
      const outcome = confirmOutcome(result);
      if (outcome.kind === "paid") {
        onPaid(outcome.paymentIntentId);
        return;
      }
      setError(outcome.message);
    } catch {
      setError("Something went wrong taking your payment. Please try again.");
    }
    setSubmitting(false);
    onBusy(false);
  }

  const disabled = !stripe || !ready || submitting;
  return (
    <form onSubmit={submit}>
      <PaymentElement onReady={() => setReady(true)} options={{ layout: "tabs" }} />
      {error && (
        <div role="alert" style={styles.error}>
          {error}
        </div>
      )}
      <button type="submit" disabled={disabled} style={submitStyle(primary, disabled)}>
        {submitting ? "Processing…" : `Pay Now${amountLabel ? ` · ${amountLabel}` : ""}`}
      </button>
    </form>
  );
}
